import { CircularProgress, MenuItem, TextField } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import PropTypes from 'prop-types'
import React from 'react'

import Button from 'app/components/generic/Button'

import { EMPTY_MANUAL_ROW, TIME_SLOTS } from './config'
import {
  Card,
  FieldRow,
  RemoveButton,
  RowCard,
  RowHeader,
  RowLabel,
  SectionLabel
} from './styles'

const inputProps = {
  variant: 'outlined',
  size: 'small',
  fullWidth: true,
  InputLabelProps: { shrink: true }
}

const ManualRowsPanel = (props) => {
  const { rows, onChange, centres, courses, teachers, rooms, loading } = props

  const updateRow = (index, patch) => {
    const next = rows.map((row, i) =>
      i === index ? { ...row, ...patch } : row
    )
    onChange(next)
  }

  const addRow = () => onChange([...rows, { ...EMPTY_MANUAL_ROW }])

  const removeRow = (index) => {
    if (rows.length === 1) {
      onChange([{ ...EMPTY_MANUAL_ROW }])
      return
    }
    onChange(rows.filter((_, i) => i !== index))
  }

  return (
    <Card>
      <SectionLabel>
        rows {loading ? <CircularProgress size={12} /> : null}
      </SectionLabel>
      {rows.map((row, idx) => {
        const centreUuid = row.centre?.uuid
        const filteredRooms = centreUuid
          ? rooms.filter((r) => r.centre_uuid === centreUuid)
          : []

        return (
          <RowCard key={idx}>
            <RowHeader>
              <RowLabel>row {idx + 1}</RowLabel>
              <RemoveButton type="button" onClick={() => removeRow(idx)}>
                remove
              </RemoveButton>
            </RowHeader>

            <FieldRow $cols={3}>
              <Autocomplete
                options={centres}
                value={row.centre}
                getOptionLabel={(o) => o?.name || ''}
                getOptionSelected={(a, b) => a?.uuid === b?.uuid}
                onChange={(_, value) =>
                  updateRow(idx, { centre: value, room: null })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...inputProps}
                    label="Centre"
                    placeholder="Sarjapur"
                    required
                  />
                )}
              />
              <Autocomplete
                options={courses}
                value={row.course}
                getOptionLabel={(o) => o?.name || ''}
                getOptionSelected={(a, b) => a?.uuid === b?.uuid}
                onChange={(_, value) => updateRow(idx, { course: value })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...inputProps}
                    label="Playdate Subject Name"
                    placeholder="Storytelling Camp"
                    required
                  />
                )}
              />
              <Autocomplete
                options={filteredRooms}
                value={row.room}
                disabled={!centreUuid}
                getOptionLabel={(o) => o?.name || ''}
                getOptionSelected={(a, b) => a?.uuid === b?.uuid}
                onChange={(_, value) => updateRow(idx, { room: value })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...inputProps}
                    label="Space / Room"
                    placeholder={centreUuid ? 'Room 12' : 'select centre first'}
                    required
                  />
                )}
              />
            </FieldRow>

            <FieldRow $cols={3}>
              <Autocomplete
                options={teachers}
                value={row.teacher}
                getOptionLabel={(o) =>
                  o
                    ? `${o.full_name}${
                        o.phone_number ? ` (${o.phone_number})` : ''
                      }`
                    : ''
                }
                getOptionSelected={(a, b) => a?.uuid === b?.uuid}
                onChange={(_, value) => updateRow(idx, { teacher: value })}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    {...inputProps}
                    label="Teacher"
                    placeholder="search by name"
                    required
                  />
                )}
              />
              <TextField
                {...inputProps}
                type="date"
                label="Start Date"
                value={row.startDate}
                onChange={(e) => updateRow(idx, { startDate: e.target.value })}
                required
              />
              <TextField
                {...inputProps}
                type="date"
                label="End Date"
                value={row.endDate}
                onChange={(e) => updateRow(idx, { endDate: e.target.value })}
                required
              />
            </FieldRow>

            <FieldRow $cols={3}>
              <TextField
                {...inputProps}
                type="time"
                label="Start Time"
                value={row.startTime}
                onChange={(e) => updateRow(idx, { startTime: e.target.value })}
                required
              />
              <TextField
                {...inputProps}
                type="time"
                label="End Time"
                value={row.endTime}
                onChange={(e) => updateRow(idx, { endTime: e.target.value })}
                required
              />
              <TextField
                {...inputProps}
                select
                label="Time Slot"
                value={row.timeSlot}
                onChange={(e) => updateRow(idx, { timeSlot: e.target.value })}
              >
                <MenuItem value="">
                  <em>none</em>
                </MenuItem>
                {TIME_SLOTS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>
            </FieldRow>

            <FieldRow $cols={3}>
              <TextField
                {...inputProps}
                type="number"
                label="Capacity"
                value={row.capacity}
                onChange={(e) => updateRow(idx, { capacity: e.target.value })}
                inputProps={{ min: 0 }}
              />
              <TextField
                {...inputProps}
                type="number"
                label="Price"
                value={row.price}
                onChange={(e) => updateRow(idx, { price: e.target.value })}
                inputProps={{ min: 0 }}
              />
              <TextField
                {...inputProps}
                type="number"
                label="Price per session"
                value={row.pricePerSession}
                onChange={(e) =>
                  updateRow(idx, { pricePerSession: e.target.value })
                }
                inputProps={{ min: 0 }}
              />
            </FieldRow>

            <FieldRow $cols={2}>
              <TextField
                {...inputProps}
                label="Age Group"
                placeholder="5-7"
                value={row.ageGroup}
                onChange={(e) => updateRow(idx, { ageGroup: e.target.value })}
              />
              <TextField
                {...inputProps}
                label="Grade Group"
                placeholder="1-3"
                value={row.gradeGroup}
                onChange={(e) => updateRow(idx, { gradeGroup: e.target.value })}
              />
            </FieldRow>
          </RowCard>
        )
      })}
      <Button onClick={addRow}>add row</Button>
    </Card>
  )
}

ManualRowsPanel.propTypes = {
  rows: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  centres: PropTypes.array.isRequired,
  courses: PropTypes.array.isRequired,
  teachers: PropTypes.array.isRequired,
  rooms: PropTypes.array.isRequired,
  loading: PropTypes.bool
}

export default ManualRowsPanel
